import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  dashboardsService,
  customMetricsService,
  DashboardConfig,
  DashboardWidget,
} from '../services/dashboards.service';

// ==================== Query Keys ====================

export const dashboardKeys = {
  all: ['dashboards'] as const,
  lists: () => [...dashboardKeys.all, 'list'] as const,
  detail: (id: string) => [...dashboardKeys.all, 'detail', id] as const,
  catalog: () => [...dashboardKeys.all, 'catalog'] as const,
  annotations: (id: string) => [...dashboardKeys.all, 'annotations', id] as const,
};

export const customMetricKeys = {
  all: ['custom-metrics'] as const,
  lists: () => [...customMetricKeys.all, 'list'] as const,
  detail: (id: string) => [...customMetricKeys.all, 'detail', id] as const,
  evaluate: (id: string) => [...customMetricKeys.all, 'evaluate', id] as const,
  timeSeries: (id: string) => [...customMetricKeys.all, 'time-series', id] as const,
};

// ==================== Dashboard Hooks ====================

export function useDashboardList() {
  return useQuery({
    queryKey: dashboardKeys.lists(),
    queryFn: () => dashboardsService.listDashboards(),
  });
}

export function useDashboard(id: string) {
  return useQuery({
    queryKey: dashboardKeys.detail(id),
    queryFn: () => dashboardsService.getDashboard(id),
    enabled: !!id,
  });
}

export function useWidgetCatalog() {
  return useQuery({
    queryKey: dashboardKeys.catalog(),
    queryFn: () => dashboardsService.getWidgetCatalog(),
    staleTime: 5 * 60 * 1000, // 5 min (catalog rarely changes)
  });
}

export function useSeedDefaultDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => dashboardsService.seedDefault(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

export function useCreateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: { name: string; roleType?: string; settings?: Record<string, any> }) =>
      dashboardsService.createDashboard(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

export function useUpdateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dto }: { id: string; name?: string; layout?: any[]; settings?: Record<string, any> }) =>
      dashboardsService.updateDashboard(id, dto),
    onSuccess: (data) => {
      queryClient.setQueryData(dashboardKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

export function useDeleteDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dashboardsService.deleteDashboard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

// ==================== Widget Hooks ====================

export function useAddWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, ...dto }: {
      dashboardId: string;
      widgetType: string;
      title: string;
      config?: Record<string, any>;
      position?: { x: number; y: number; w: number; h: number };
    }) => dashboardsService.addWidget(dashboardId, dto),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(dashboardId) });
    },
  });
}

export function useUpdateWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, widgetId, ...dto }: {
      dashboardId: string;
      widgetId: string;
      title?: string;
      config?: Record<string, any>;
      position?: { x: number; y: number; w: number; h: number };
      isVisible?: boolean;
    }) => dashboardsService.updateWidget(dashboardId, widgetId, dto),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(dashboardId) });
    },
  });
}

export function useRemoveWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, widgetId }: { dashboardId: string; widgetId: string }) =>
      dashboardsService.removeWidget(dashboardId, widgetId),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(dashboardId) });
    },
  });
}

// ==================== Org Default ====================

export function useSetOrgDefault() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, roleType }: { dashboardId: string; roleType: string }) =>
      dashboardsService.setOrgDefault(dashboardId, roleType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

// ==================== Annotations ====================

export function useDashboardAnnotations(dashboardId: string) {
  return useQuery({
    queryKey: dashboardKeys.annotations(dashboardId),
    queryFn: () => dashboardsService.listAnnotations(dashboardId),
    enabled: !!dashboardId,
  });
}

export function useAddAnnotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, ...dto }: {
      dashboardId: string;
      widgetId?: string;
      dataPoint?: Record<string, any>;
      content: string;
    }) => dashboardsService.addAnnotation(dashboardId, dto),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.annotations(dashboardId) });
    },
  });
}

// ==================== Custom Metric Hooks ====================

export function useCustomMetricsList(activeOnly = true) {
  return useQuery({
    queryKey: [...customMetricKeys.lists(), { activeOnly }],
    queryFn: () => customMetricsService.listMetrics(activeOnly),
  });
}

export function useCustomMetric(id: string) {
  return useQuery({
    queryKey: customMetricKeys.detail(id),
    queryFn: () => customMetricsService.getMetric(id),
    enabled: !!id,
  });
}

export function useCreateCustomMetric() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: customMetricsService.createMetric.bind(customMetricsService),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customMetricKeys.lists() });
    },
  });
}

export function useUpdateCustomMetric() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dto }: { id: string } & Partial<any>) =>
      customMetricsService.updateMetric(id, dto),
    onSuccess: (data) => {
      queryClient.setQueryData(customMetricKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: customMetricKeys.lists() });
    },
  });
}

export function useDeleteCustomMetric() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customMetricsService.deleteMetric(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customMetricKeys.lists() });
    },
  });
}

export function useEvaluateMetric(id: string, startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: [...customMetricKeys.evaluate(id), { startDate, endDate }],
    queryFn: () => customMetricsService.evaluateMetric(id, startDate, endDate),
    enabled: !!id,
  });
}

export function useMetricTimeSeries(id: string, startDate?: string, endDate?: string, interval = 'day') {
  return useQuery({
    queryKey: [...customMetricKeys.timeSeries(id), { startDate, endDate, interval }],
    queryFn: () => customMetricsService.getTimeSeries(id, startDate, endDate, interval),
    enabled: !!id,
  });
}

export function usePreviewMetric() {
  return useMutation({
    mutationFn: customMetricsService.previewMetric.bind(customMetricsService),
  });
}

export function useGenerateQuerySpec() {
  return useMutation({
    mutationFn: customMetricsService.generateQuerySpec.bind(customMetricsService),
  });
}
